import { useEffect } from "react";
import { observer } from "mobx-react-lite";
import { useLocation } from "react-router-dom";
import { useTranslation } from "../i18n/useTranslation.js";
import { PUBLIC_PATHS, type PublicPath } from "../config/productConfig.js";
import { getSeoData } from "./seoConfig.js";

const upsertMeta = (key: "name" | "property", label: string, value: string) => {
  const selector = `meta[${key}="${label}"]`;
  let element = document.head.querySelector<HTMLMetaElement>(selector);
  if (!element) {
    element = document.createElement("meta");
    element.setAttribute(key, label);
    document.head.appendChild(element);
  }
  element.content = value;
};

const updateCanonical = (url: string) => {
  let link = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!link) {
    link = document.createElement("link");
    link.rel = "canonical";
    document.head.appendChild(link);
  }
  link.href = url;
};

const isPublicPath = (path: string): path is PublicPath =>
  (PUBLIC_PATHS as readonly string[]).includes(path);

const LandingSeo = observer(() => {
  const { pathname } = useLocation();
  const { language, t } = useTranslation();
  useEffect(() => {
    if (!isPublicPath(pathname)) return;
    const data = getSeoData(pathname, t, language);
    document.documentElement.lang = language;
    document.title = data.title;
    upsertMeta("name", "description", data.description);
    upsertMeta("property", "og:title", data.title);
    upsertMeta("property", "og:description", data.description);
    upsertMeta("property", "og:image", data.image);
    upsertMeta("property", "og:url", data.canonical);
    upsertMeta("property", "og:locale", data.openGraphLocale);
    updateCanonical(data.canonical);
  }, [language, pathname, t]);
  return null;
});

export default LandingSeo;
